import {
  AtContainerWidth,
  Button,
  Divider,
  Ellipsis,
  IconButton,
  ResponsiveTableRow,
  Row,
  Separator,
  Toggle,
  ToggleGroup,
  H5,
} from '@fs/zion-ui'
import React, { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowChange, ContentDelete, ContentDownload, ControlView } from '@fs/zion-icon/dist/cjs/icons'
import { css } from '@emotion/core'
import { usePageView } from '@fs/zion-analytics'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import useStreamDownload from '../../../hooks/useStreamDownload'
import useNotifications from '../../../hooks/useNotifications'
import { checkStatus, deleteResults, getQueriesList } from '../../../services/rftService'
import { convertUTCString } from '../../../util/DateUtil'
import { camelCaseToReadable } from '../../../util/StringUtil'
import RftSubmitQuery from './RftSubmitQuery'
import getUserSummary from '../../../services/userService'
import { RftContext } from './RftContext'
import { getBackendBaseUrl } from '../../../util/UrlUtil'
import DmcPaginator from '../../common/DmcPaginator'
import { usePageTitle } from '../../app/PageTitleContext'

export default function RftQueryResults() {
  usePageView({ page_detail: 'DMConsole:RftQueryResults' })
  const MAX_RESULTS = 100
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.tools.rftQueryResults'))
  const { error } = useNotifications()
  const { state, dispatch } = useContext(RftContext)

  const iconsCss = css`
  display: flex;
  justify-content: flex-end;
  margin-inline-end: 30px;
}`

  useEffect(() => {
    getQueriesList(state.page + 1, state.pageCount, state.showAll, state.oldestToNewest)
      .then((response) => {
        dispatch({ type: 'SET_RESULTS_LIST', value: response.data })
      })
      .catch((err) => {
        error(t('rft.queriesList.error', 'Error retrieving jobs'), err)
      })
  }, [dispatch, error, state.oldestToNewest, state.page, state.pageCount, state.showAll, t])

  useEffect(() => {
    async function fetchData() {
      const newMapValues = {}
      await Promise.all(
        [...state.newIds].map(async (id) => {
          await getUserSummary(id, 'cisUserId')
            .then((result) => {
              newMapValues[id] = result.data?.dmcUser?.displayName || result.data?.dmcUser?.contactName
            })
            .catch((err) => {
              error(t('rft.user.errorRetrievingUsername', 'Error retrieving user name'), err)
            })
        })
      )
      dispatch({ type: 'SET_NAMES_MAP', value: newMapValues })
    }
    fetchData()
  }, [dispatch, error, state.newIds, t])

  const checkStatusHandler = (jobId) => {
    checkStatus(jobId)
      .then((response) => {
        dispatch({ type: 'UPDATE_JOB_STATUS', jobId, value: response.data })
      })
      .catch((err) => {
        error(t('rft.checkStatus.error', 'Error retrieving status for this query'), err)
      })
  }

  const deleteResultsHandler = (jobId) => {
    deleteResults(jobId)
      .then(() => {
        dispatch({ type: 'DELETE_JOB', value: jobId })
      })
      .catch((err) => {
        error(t('rft.deleteResults.error', 'Error deleting results'), err)
      })
  }

  const refreshStatus = () => {
    getQueriesList(state.page + 1, state.pageCount, state.showAll, state.oldestToNewest)
      .then((response) => {
        dispatch({ type: 'SET_RESULTS_LIST', value: response.data })
      })
      .catch((err) => {
        error(t('rft.queriesList.error', 'Error retrieving jobs'), err)
      })
  }

  const handleShowAll = ({ target }) => dispatch({ type: 'TOGGLE_SHOW_ALL', value: target.checked })
  const handleOldestToNewest = ({ target }) => dispatch({ type: 'TOGGLE_OLDEST', value: target.checked })

  const { streamDataDownload } = useStreamDownload()

  const downloadRftResults = (jobId) => {
    streamDataDownload({
      requestPath: `${getBackendBaseUrl()}/rft/download-results/${jobId}`,
    })
  }

  const getIcons = (result, isRow) => {
    return (
      <span css={iconsCss}>
        <RowOrColumn isRow={isRow}>
          {result.runStatus === 'Successful' && (
            <RowOrColumn isRow={isRow}>
              <IconButton
                Icon={ControlView}
                title={t('rft.icon.resultsPreview', 'Results Preview')}
                to={`/tools/rftQueries/queries/${result.jobId}`}
              />
              <IconButton
                Icon={ContentDownload}
                title={t('rft.icon.downloadResults', 'Download Results')}
                onClick={() => downloadRftResults(result.jobId)}
              />
            </RowOrColumn>
          )}
          {result.runStatus === 'Running' && (
            <IconButton
              Icon={ArrowChange}
              title={t('rft.icon.refreshStatus', 'Refresh Status')}
              onClick={() => checkStatusHandler(result.jobId)}
            />
          )}

          <IconButton
            Icon={ContentDelete}
            title={t('rft.icon.deleteJob', 'Delete Job')}
            onClick={() => deleteResultsHandler(result.jobId)}
            emphasis="high"
            color="red"
          />
        </RowOrColumn>
      </span>
    )
  }

  return (
    <>
      {!state.showSubmit && (
        <Button emphasis="medium" onClick={() => dispatch({ type: 'TOGGLE_SHOW_SUBMIT' })}>
          {t('rft.results.newQuery', 'New Query')}
        </Button>
      )}
      {state.showSubmit && <RftSubmitQuery state={state} dispatch={dispatch} />}
      <Separator />
      <Divider />
      <H5>{t('rft.queries.viewAdminQueryResults', 'View Admin Query Results')}</H5>
      <Separator />
      <Row>
        <Button Icon={ArrowChange} emphasis="medium" onClick={refreshStatus}>
          {t('rft.results.refreshStatus', 'Refresh Status')}
        </Button>
        <ToggleGroup orientation="horizontal">
          <Toggle
            label={t('rft.queries.showAll', 'Show All')}
            name="showAll"
            checked={state.showAll}
            onChange={handleShowAll}
          />
          <Toggle
            label={t('rft.queries.oldestToNewest', 'Oldest to Newest')}
            name="oldestToNewest"
            checked={state.oldestToNewest}
            onChange={handleOldestToNewest}
          />
        </ToggleGroup>
      </Row>
      {state.resultsList && state.totalElements && (
        <DmcPaginator
          maxPageCount={MAX_RESULTS}
          length={state.totalElements}
          pageCount={state.pageCount}
          setPageCount={(value) => {
            if (value !== state.pageCount) {
              dispatch({ type: 'SET_PAGE_COUNT', value })
            }
          }}
          index={state.page}
          setIndex={(value) => dispatch({ type: 'SET_PAGE', value })}
          countKey="RftPaginator"
        >
          <DmcResponsiveTable
            data-testid="rftTable"
            totalColumnCount={6}
            atSizeColumns={{
              default: {
                labels: [
                  t('rft.table.function', 'Function'),
                  t('rft.table.parameters', 'Parameters'),
                  t('rft.table.requestor', 'Requestor'),
                  t('rft.table.startTime', 'Start Time'),
                  t('rft.table.status', 'Status'),
                  '',
                ],
                columnWidths: [2, 2, 2, 2, 2, 2],
              },
            }}
          >
            {state.resultsList?.map((result) => {
              const queryParamsString =
                result.queryParams &&
                Object.entries(result.queryParams)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join(', ')
              return (
                <ResponsiveTableRow
                  key={result.jobId}
                  name={result.fileName}
                  column1={<Ellipsis>{camelCaseToReadable(result.queryName)}</Ellipsis>}
                  column2={<Ellipsis>{queryParamsString}</Ellipsis>}
                  column3={
                    <>
                      <Row>{result.requesterDisplayName || state.namesMap[result.requesterCisId]}</Row>
                      <Row>
                        <span>({result.requesterCisId})</span>
                      </Row>
                    </>
                  }
                  column4={<Ellipsis>{convertUTCString(result.startTime, true)}</Ellipsis>}
                  column5={<Ellipsis>{result.runStatus}</Ellipsis>}
                  column6={
                    <AtContainerWidth>
                      {(atWidth) =>
                        atWidth({
                          default: getIcons(result, true),
                          nano: getIcons(result, false),
                          xxs: getIcons(result, false),
                          xs: getIcons(result, false),
                          sm: getIcons(result, false),
                          md: getIcons(result, false),
                          lg: getIcons(result, false),
                          xl: getIcons(result, true),
                          xxl: getIcons(result, true),
                        })
                      }
                    </AtContainerWidth>
                  }
                />
              )
            })}
          </DmcResponsiveTable>
        </DmcPaginator>
      )}
      {state.resultsList?.length === 0 && (
        <>
          <Separator />
          <div>{t('rft.queries.noQueriesFound', 'No Queries Found')}</div>
        </>
      )}
    </>
  )
}

const RowOrColumn = ({ isRow, children }) => (isRow ? <Row>{children}</Row> : <span>{children}</span>)
